import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../utils/api';
import { useToast } from '../shared/Toast';
import { HiOutlineFilm, HiOutlineTrash, HiOutlineDownload, HiOutlineRefresh } from 'react-icons/hi';

const STATUS_BADGE = {
  processing: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
};

export default function MarketingVideos() {
  const { addToast } = useToast();
  const [connected, setConnected] = useState(null);
  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [title, setTitle] = useState('');
  const [script, setScript] = useState('');
  const [avatarId, setAvatarId] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [creating, setCreating] = useState(false);
  const [videos, setVideos] = useState([]);
  const pollRef = useRef(null);

  const loadVideos = useCallback(() => {
    api.get('/marketing/videos').then(({ data }) => setVideos(data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/marketing/heygen/status').then(({ data }) => {
      setConnected(data.connected);
      if (data.connected) {
        api.get('/marketing/heygen/avatars').then(({ data }) => { setAvatars(data); if (data[0]) setAvatarId(data[0].avatar_id); }).catch(() => {});
        api.get('/marketing/heygen/voices').then(({ data }) => { setVoices(data); if (data[0]) setVoiceId(data[0].voice_id); }).catch(() => {});
      }
    }).catch(() => setConnected(false));
    loadVideos();
  }, [loadVideos]);

  // Poll processing videos
  useEffect(() => {
    const processing = videos.filter(v => v.status === 'processing' && v.heygenVideoId);
    if (processing.length === 0) return;
    pollRef.current = setInterval(async () => {
      for (const v of processing) {
        try {
          const { data } = await api.get(`/marketing/videos/${v.id}/status`);
          if (data.status !== 'processing') loadVideos();
        } catch { /* ignore */ }
      }
    }, 6000);
    return () => clearInterval(pollRef.current);
  }, [videos, loadVideos]);

  const handleCreate = async () => {
    if (!title.trim() || !script.trim() || !avatarId || !voiceId) {
      addToast('Title, script, avatar and voice are all required', 'error'); return;
    }
    setCreating(true);
    try {
      const avatarName = avatars.find(a => a.avatar_id === avatarId)?.name;
      await api.post('/marketing/videos', { title, script, avatarId, avatarName, voiceId });
      addToast('Video render started (this can take a few minutes)');
      setTitle(''); setScript('');
      loadVideos();
    } catch (err) {
      addToast(err.response?.data?.error || 'Failed to start render', 'error');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (v) => {
    if (!window.confirm('Delete this video?')) return;
    try { await api.delete(`/marketing/videos/${v.id}`); loadVideos(); }
    catch { addToast('Delete failed', 'error'); }
  };

  if (connected === false) {
    return (
      <div className="max-w-3xl">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">AI Videos</h1>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-amber-800">
          HeyGen is not configured. Add <code>heygen_api_key</code> in admin Settings to enable video generation.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">AI Videos</h1>
      <p className="text-gray-500 mb-6">Turn a script into a HeyGen avatar video.</p>

      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-8 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
          <input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. V2 Cloud in 60 seconds"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Script</label>
          <textarea value={script} onChange={e => setScript(e.target.value)} rows={5} placeholder="What should the avatar say?"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Avatar</label>
            <select value={avatarId} onChange={e => setAvatarId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              {avatars.length === 0 && <option value="">Loading avatars...</option>}
              {avatars.map(a => <option key={a.avatar_id} value={a.avatar_id}>{a.name || a.avatar_id}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Voice</label>
            <select value={voiceId} onChange={e => setVoiceId(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
              {voices.length === 0 && <option value="">Loading voices...</option>}
              {voices.map(v => <option key={v.voice_id} value={v.voice_id}>{v.name} {v.language ? `(${v.language})` : ''}</option>)}
            </select>
          </div>
        </div>
        <button onClick={handleCreate} disabled={creating || connected === null}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-v2-blue text-white rounded-lg font-medium disabled:opacity-50">
          <HiOutlineFilm className="w-5 h-5" /> {creating ? 'Starting...' : 'Generate video'}
        </button>
      </div>

      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Your videos</h2>
        <button onClick={loadVideos} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1">
          <HiOutlineRefresh className="w-4 h-4" /> Refresh
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {videos.length === 0 && <div className="col-span-full p-8 text-center text-gray-400 text-sm bg-white rounded-lg border border-gray-200">No videos yet.</div>}
        {videos.map(v => (
          <div key={v.id} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {v.status === 'completed' && v.videoUrl ? (
              <video src={v.videoUrl} poster={v.thumbnailUrl || undefined} controls className="w-full bg-black aspect-video" />
            ) : (
              <div className="w-full aspect-video bg-gray-100 flex items-center justify-center">
                {v.status === 'processing'
                  ? <div className="text-center text-gray-500 text-sm"><div className="w-8 h-8 border-4 border-v2-blue border-t-transparent rounded-full animate-spin mx-auto mb-2" />Rendering...</div>
                  : <span className="text-red-500 text-sm px-4 text-center">{v.errorMessage || 'Failed'}</span>}
              </div>
            )}
            <div className="p-3 flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm text-gray-900 truncate">{v.title}</div>
                <div className="text-xs text-gray-400">{v.avatarName || v.avatarId} · {new Date(v.createdAt).toLocaleDateString()}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[v.status] || 'bg-gray-100 text-gray-600'}`}>{v.status}</span>
              {v.status === 'completed' && v.videoUrl && (
                <a href={v.videoUrl} target="_blank" rel="noopener noreferrer" download title="Download" className="p-1.5 text-gray-500 hover:text-v2-blue"><HiOutlineDownload className="w-5 h-5" /></a>
              )}
              <button onClick={() => handleDelete(v)} title="Delete" className="p-1.5 text-red-400 hover:text-red-600"><HiOutlineTrash className="w-5 h-5" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
